import React from 'react';
import { Select, Datepicker } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { MONTHS, PAST_DAYS_OPTIONS } from 'Utils';
import { DATE_RANGES_TYPES } from 'Common/enums';
import styles from './DateRange.css';

type Props = {
    dateRangeObj: DateRangeObject;
    updateDateRangeValue: (value: DateRangeValue) => void;
    yearsOptions: FieldOptions[];
    dateRangeValueErrs?: DateRangeValueError;
};

const getNewMonthSelectValue = (oldValue: MonthDateRangeTypeValue, source: 'year' | 'month', newValue: number) => {
    if (source === 'month') {
        return {
            ...oldValue,
            month: newValue
        };
    }
    return {
        ...oldValue,
        year: newValue
    };
};

const SubFields = ({ dateRangeObj, updateDateRangeValue, yearsOptions = [], dateRangeValueErrs }: Props) => {
    /** dateRangeValueErrs is never used. Please fix it if you know how to implement Date Range errors. */
    console.log(dateRangeValueErrs);
    const { dateRangeType, dateRangeValue } = dateRangeObj;
    const { t } = useTranslation();

    switch (dateRangeType) {
        case DATE_RANGES_TYPES.CUSTOM: {
            return (
                <div data-testid='custom-daterange-picker' className={styles.customRangePicker}>
                    <Datepicker
                        type='range'
                        value={dateRangeValue || { startDate: null, endDate: null }}
                        size='sm'
                        handleChange={(value) => {
                            updateDateRangeValue(value);
                        }}
                        editable
                        maxDate={new Date()}
                    />
                </div>
            );
        }
        case DATE_RANGES_TYPES.PAST_DAYS: {
            return (
                <Select
                    dataTestId='past-days-select'
                    className={styles.dateRangeSubSelect}
                    placeholder='-Select-'
                    selectedItems={[dateRangeValue as string]}
                    handleClickCallback={(value) => updateDateRangeValue(value[0])}
                    size='sm'
                >
                    {PAST_DAYS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {t(option.label)}
                        </option>
                    ))}
                </Select>
            );
        }
        case DATE_RANGES_TYPES.MONTH: {
            return (
                <div className={styles.flexRow}>
                    <Select
                        dataTestId='daterange-month-select'
                        className={styles.dateRangeSubSelect}
                        placeholder='-Select-'
                        selectedItems={[(dateRangeValue as MonthDateRangeTypeValue)?.month]}
                        size='sm'
                        handleClickCallback={(value) => {
                            updateDateRangeValue(
                                getNewMonthSelectValue(
                                    dateRangeValue as MonthDateRangeTypeValue,
                                    'month',
                                    Number(value[0])
                                )
                            );
                        }}
                    >
                        {MONTHS.map((option) => (
                            <option key={option?.value} value={option?.value}>
                                {t(option?.label)}
                            </option>
                        ))}
                    </Select>
                    <Select
                        dataTestId='sub-year-select'
                        className={styles.subMonthYearSelect}
                        placeholder='-Select-'
                        selectedItems={[(dateRangeValue as MonthDateRangeTypeValue)?.year]}
                        size='sm'
                        handleClickCallback={(value) => {
                            updateDateRangeValue(
                                getNewMonthSelectValue(
                                    dateRangeValue as MonthDateRangeTypeValue,
                                    'year',
                                    Number(value[0])
                                )
                            );
                        }}
                    >
                        {yearsOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
            );
        }
        case DATE_RANGES_TYPES.YEAR: {
            return (
                <Select
                    dataTestId='daterange-year-select'
                    className={styles.dateRangeSubSelect}
                    placeholder='-Select-'
                    selectedItems={[dateRangeValue as number]}
                    size='sm'
                    handleClickCallback={(value) => updateDateRangeValue(Number(value[0]))}
                >
                    {yearsOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Select>
            );
        }
        case DATE_RANGES_TYPES.MONTH_TO_DATE:
        case DATE_RANGES_TYPES.YEAR_TO_DATE:
        default: {
            return null;
        }
    }
};

export default SubFields;
